'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { Activity, CheckCircle2, XCircle, AlertTriangle, Info, RefreshCw, Filter } from 'lucide-react';

interface LogEntry {
  id: string;
  timestamp: string;
  level: 'success' | 'error' | 'warning' | 'info';
  source: string;
  message: string;
  details?: string;
}

const mockLogs: LogEntry[] = [
  { id: '1', timestamp: '2026-08-03 04:58:12', level: 'success', source: 'Meta Publisher', message: 'Reel published successfully', details: 'Reel ID: 17841426405856463 — "5 AI Tools That Replace Your Entire Team"' },
  { id: '2', timestamp: '2026-08-03 04:55:03', level: 'success', source: 'OpenAI Script Gen', message: 'Script generated in 3.2s', details: 'Signal: "AI productivity tools" — 847 tokens used' },
  { id: '3', timestamp: '2026-08-03 04:54:47', level: 'info', source: 'Trend Ingestion', message: 'New signal detected: velocity 94/100', details: 'Keyword: "AI productivity tools" — Source: TikTok trending' },
  { id: '4', timestamp: '2026-08-03 04:50:21', level: 'warning', source: 'Render Pipeline', message: 'Render queue depth at 78% capacity', details: '7 of 9 slots occupied — consider scaling render workers' },
  { id: '5', timestamp: '2026-08-03 04:47:09', level: 'error', source: 'Meta Publisher', message: 'Publish failed — rate limit hit', details: 'HTTP 429 from Graph API — retry scheduled in 15 min' },
  { id: '6', timestamp: '2026-08-03 04:44:33', level: 'success', source: 'DM Funnel', message: '3 DM keyword triggers fired', details: 'Keywords: "link", "info", "how" — 3 auto-replies sent' },
  { id: '7', timestamp: '2026-08-03 04:40:00', level: 'info', source: 'Cron Scheduler', message: 'Pipeline cycle started', details: 'Cycle #142 — interval: 15 min' },
  { id: '8', timestamp: '2026-08-03 04:38:55', level: 'success', source: 'Meta Publisher', message: 'Reel published successfully', details: 'Reel ID: 17841426405856464 — "ChatGPT Prompt Engineering Secrets"' },
  { id: '9', timestamp: '2026-08-03 04:35:12', level: 'success', source: 'OpenAI Script Gen', message: 'Script generated in 2.8s', details: 'Signal: "ChatGPT prompts" — 712 tokens used' },
  { id: '10', timestamp: '2026-08-03 04:33:40', level: 'warning', source: 'Trend Ingestion', message: 'Signal confidence below threshold', details: 'Keyword: "crypto trading bots" — confidence: 42% (min: 60%)' },
  { id: '11', timestamp: '2026-08-03 04:30:00', level: 'info', source: 'Cron Scheduler', message: 'Pipeline cycle started', details: 'Cycle #141 — interval: 15 min' },
  { id: '12', timestamp: '2026-08-03 04:28:17', level: 'success', source: 'DM Funnel', message: 'Engagement spike detected', details: '127 new comments in 10 min on Reel #17841426405856462' },
  { id: '13', timestamp: '2026-08-03 04:25:44', level: 'error', source: 'Render Pipeline', message: 'Render worker timeout', details: 'Worker #3 timed out after 120s — job requeued' },
  { id: '14', timestamp: '2026-08-03 04:22:09', level: 'success', source: 'Meta Publisher', message: 'Reel published successfully', details: 'Reel ID: 17841426405856462 — "Make $500/Day with AI Side Hustles"' },
  { id: '15', timestamp: '2026-08-03 04:20:00', level: 'info', source: 'Cron Scheduler', message: 'Pipeline cycle started', details: 'Cycle #140 — interval: 15 min' },
];

const levelConfig = {
  success: { icon: CheckCircle2, color: 'text-emerald-400', bg: 'bg-emerald-400/10 border-emerald-400/20', label: 'Success' },
  error: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-400/10 border-red-400/20', label: 'Error' },
  warning: { icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-400/10 border-amber-400/20', label: 'Warning' },
  info: { icon: Info, color: 'text-sky-400', bg: 'bg-sky-400/10 border-sky-400/20', label: 'Info' },
};

const sourceColors: Record<string, string> = {
  'Meta Publisher': 'bg-violet-500/15 text-violet-300 border-violet-500/20',
  'OpenAI Script Gen': 'bg-emerald-500/15 text-emerald-300 border-emerald-500/20',
  'Trend Ingestion': 'bg-sky-500/15 text-sky-300 border-sky-500/20',
  'Render Pipeline': 'bg-orange-500/15 text-orange-300 border-orange-500/20',
  'DM Funnel': 'bg-pink-500/15 text-pink-300 border-pink-500/20',
  'Cron Scheduler': 'bg-slate-500/15 text-slate-300 border-slate-500/20',
};

export default function ActivityLogPage() {
  const [filter, setFilter] = useState<'all' | 'success' | 'error' | 'warning' | 'info'>('all');
  const [expanded, setExpanded] = useState<string | null>(null);

  const filtered = filter === 'all' ? mockLogs : mockLogs.filter((l) => l.level === filter);

  const counts = {
    success: mockLogs.filter((l) => l.level === 'success').length,
    error: mockLogs.filter((l) => l.level === 'error').length,
    warning: mockLogs.filter((l) => l.level === 'warning').length,
    info: mockLogs.filter((l) => l.level === 'info').length,
  };

  return (
    <AppLayout>
      <div className="space-y-6 fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
              <Activity size={18} className="text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-foreground">Activity Log</h1>
              <p className="text-xs text-muted-foreground font-mono">Real-time pipeline event stream</p>
            </div>
          </div>
          <button className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-secondary border border-border text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
            <RefreshCw size={13} />
            Refresh
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-4 gap-3">
          {(['success', 'error', 'warning', 'info'] as const).map((level) => {
            const cfg = levelConfig[level];
            const LevelIcon = cfg.icon;
            return (
              <button
                key={level}
                onClick={() => setFilter(filter === level ? 'all' : level)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg border transition-all ${
                  filter === level ? cfg.bg : 'bg-card border-border hover:bg-muted'
                }`}
              >
                <LevelIcon size={16} className={cfg.color} />
                <div className="text-left">
                  <p className="text-xs text-muted-foreground capitalize">{cfg.label}</p>
                  <p className="text-lg font-semibold text-foreground font-mono">{counts[level]}</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Filter Bar */}
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-card border border-border">
          <Filter size={13} className="text-muted-foreground" />
          <span className="text-xs text-muted-foreground mr-2">Filter:</span>
          {(['all', 'success', 'error', 'warning', 'info'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-2.5 py-1 rounded text-xs font-medium transition-colors capitalize ${
                filter === f
                  ? 'bg-primary/15 text-primary border border-primary/25' :'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              {f}
            </button>
          ))}
          <span className="ml-auto text-xs text-muted-foreground font-mono">{filtered.length} events</span>
        </div>

        {/* Log Table */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="grid grid-cols-[140px_80px_160px_1fr] gap-0 px-4 py-2.5 border-b border-border bg-secondary">
            <span className="text-2xs font-semibold uppercase tracking-widest text-muted-foreground">Timestamp</span>
            <span className="text-2xs font-semibold uppercase tracking-widest text-muted-foreground">Level</span>
            <span className="text-2xs font-semibold uppercase tracking-widest text-muted-foreground">Source</span>
            <span className="text-2xs font-semibold uppercase tracking-widest text-muted-foreground">Message</span>
          </div>
          <div className="divide-y divide-border">
            {filtered.map((entry) => {
              const cfg = levelConfig[entry.level];
              const LevelIcon = cfg.icon;
              const isExpanded = expanded === entry.id;
              return (
                <div
                  key={entry.id}
                  className="cursor-pointer hover:bg-muted/40 transition-colors"
                  onClick={() => setExpanded(isExpanded ? null : entry.id)}
                >
                  <div className="grid grid-cols-[140px_80px_160px_1fr] gap-0 px-4 py-3 items-center">
                    <span className="text-xs font-mono text-muted-foreground">{entry.timestamp.split(' ')[1]}</span>
                    <div className="flex items-center gap-1.5">
                      <LevelIcon size={13} className={cfg.color} />
                      <span className={`text-xs font-medium capitalize ${cfg.color}`}>{entry.level}</span>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded border font-mono w-fit ${sourceColors[entry.source] ?? 'bg-muted text-muted-foreground border-border'}`}>
                      {entry.source}
                    </span>
                    <span className="text-sm text-foreground">{entry.message}</span>
                  </div>
                  {isExpanded && entry.details && (
                    <div className="px-4 pb-3">
                      <div className="ml-[220px] px-3 py-2 rounded-md bg-secondary border border-border">
                        <p className="text-xs font-mono text-muted-foreground">{entry.details}</p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
